import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from 'framer-motion';
import { inquiryService } from "../services/inquiryService";
import ShettigreInquiryDialog from "../components/Dialog/ShettigereInquiryDialog";
import { godrejShettigereService } from "../services/godrejshettigereService";
import { Home, MapPin, Bed, CalendarDays, Building, Wallet, Phone, Ruler, Mountain, Factory, Users, Map, Forklift, ShieldCheck, Car, Train, Plane, Cloud, Briefcase, Factory as FactoryIcon2, X, Download, Leaf, DumbbellIcon, Building2 } from "lucide-react";


const Gallery = ({ godrejShettigereContent, fadeInVariants, staggerContainerVariants }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [filteredImages, setFilteredImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const galleryRef = useRef(null);

  
  const scrollbarHideStyles = `
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
    .hide-scrollbar {
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
    }
  `;


  useEffect(() => {
    if (godrejShettigereContent && godrejShettigereContent.gallery && godrejShettigereContent.gallery.images) {
      if (activeFilter === 'All') {
        setFilteredImages(godrejShettigereContent.gallery.images);
      } else {
        const filtered = godrejShettigereContent.gallery.images.filter(img => img.category === activeFilter);
        setFilteredImages(filtered);
      }
    }
  }, [activeFilter, godrejShettigereContent]);


  const handleScroll = () => {
    if (!galleryRef.current) return;
    const container = galleryRef.current;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.querySelector('.flex-shrink-0').offsetWidth + 16; 
    const index = Math.round(scrollLeft / itemWidth);
    setActiveIndex(index);
  };


  return (
    <>
      <style>{scrollbarHideStyles}</style>
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.1 }}
            variants={fadeInVariants}
            className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full"
          >
            {godrejShettigereContent.gallery.heading}
          </motion.h2>

          <div className="flex justify-center flex-wrap gap-2 sm:gap-4 mb-8 px-4">
            {['All', 'Interior', 'Clubhouse', 'Playing Area'].map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`flex-1 min-w-[120px] max-w-[200px] sm:flex-initial py-2 px-4 rounded-full text-sm sm:text-lg font-semibold transition-colors duration-300 ${
                  activeFilter === category ? 'bg-accent text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Horizontal slider for small screens */}
          <div
            className="md:hidden overflow-x-scroll hide-scrollbar pb-4"
            ref={galleryRef}
            onScroll={handleScroll}
          >
            <div className="flex space-x-4 px-6">
              {filteredImages.map((image, index) => (
                <div key={index} className="w-48 h-32 sm:w-64 sm:h-40 rounded-md overflow-hidden shadow-md flex-shrink-0">
                  <img src={image.url} alt={image.category} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Dots for small screens */}
          {filteredImages.length > 0 && (
            <div className="md:hidden flex justify-center mt-4 space-x-2">
              {filteredImages.map((_, index) => (
                <span
                  key={index}
                  className={`block w-2 h-2 rounded-full ${
                    activeIndex === index ? 'bg-accent' : 'bg-gray-400'
                  }`}
                ></span>
              ))}
            </div>
          )}

          {/* Grid layout for medium and larger screens */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-5 gap-0 overflow-hidden">
            {filteredImages.map((image, index) => (
              <div key={index} className="overflow-hidden relative group aspect-w-1 aspect-h-1"
                style={{ paddingBottom: '100%' }}>
                <img src={image.url} alt={image.category} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};


export default function GodrejShettigere() {
  const [godrejShettigereContent, setGodrejShettigereContent] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideIntervalRef = useRef(null);
  const heroImageRef = useRef(null);
  const [isScheduleVisitDialogOpen, setIsScheduleVisitDialogOpen] = useState(false);
  const [isBrochureDialogOpen, setIsBrochureDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interestedIn: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    try {
      const inquiryData = {
        property_url: window.location.href,
        property_id: 'Godrej_Shettigere',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Interested in: ${formData.interestedIn}. Additional message: ${formData.message}`,
        status: 'new'
      };

      if (type === 'brochure') {
        inquiryData.message = `Brochure Request: Interested in: ${formData.interestedIn}. Additional message: ${formData.message}`;
      } else if (type === 'scheduleVisit') {
        inquiryData.message = `Schedule Visit Request: Interested in: ${formData.interestedIn}. Additional message: ${formData.message}`;
      }


      await inquiryService.createPropertyInquiry(inquiryData);
      alert('Inquiry submitted successfully!');
      setIsScheduleVisitDialogOpen(false);
      setIsBrochureDialogOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        interestedIn: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Failed to submit inquiry. Please try again.');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
        const content = await godrejShettigereService.getGodrejContent();
        setGodrejShettigereContent(content);
    };

    fetchContent();
  }, []);

  useEffect(() => {
    if (!godrejShettigereContent || !godrejShettigereContent.heroSection.slides) return;

    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
    }

    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % godrejShettigereContent.heroSection.slides.length);
    }, 5000);


    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [currentSlide, godrejShettigereContent]);

  const fadeInVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const staggerContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  if (!godrejShettigereContent) {
    return (
        <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
    );
  }

  const iconMap = {
    Home, MapPin, Bed, CalendarDays, Building, Wallet, Phone, Ruler, Mountain, Factory, Users, Map, Forklift, ShieldCheck, Car, Train, Plane, Cloud, Briefcase, X, Download
  };

  const highlightColors = [
    { bg: 'bg-gray-50', border: 'border-gray-200' },
    { bg: 'bg-blue-50', border: 'border-blue-200' },
    { bg: 'bg-green-50', border: 'border-green-200' },
    { bg: 'bg-pink-50', border: 'border-pink-200' },
    { bg: 'bg-purple-50', border: 'border-purple-200' },
    { bg: 'bg-yellow-50', border: 'border-yellow-200' },
  ];
  
  const keyHighlightIcons = {
    "Strategic Location": MapPin,
    "World-Class Amenities": DumbbellIcon,
    "Trusted Developer": ShieldCheck,
    "Green Living": Leaf,
    "Excellent Connectivity": Train,
    "Modern Architecture": Building2,
  };


  return (
    <div className="bg-gray-50 text-gray-800 font-sans">
      <Helmet>
        <title>{godrejShettigereContent.pageTitle}</title>
      </Helmet>

      <ShettigreInquiryDialog
        isScheduleVisitDialogOpen={isScheduleVisitDialogOpen}
        setIsScheduleVisitDialogOpen={setIsScheduleVisitDialogOpen}
        isBrochureDialogOpen={isBrochureDialogOpen}
        setIsBrochureDialogOpen={setIsBrochureDialogOpen}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />

      <section className="relative h-screen overflow-hidden hero-section">
        <div className="absolute inset-0 w-full h-full">
          {godrejShettigereContent.heroSection.slides.map((slide, index) => (
            <div
              key={slide.id}
              ref={index === currentSlide ? heroImageRef : null}
              className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="absolute inset-0 w-full h-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, staggerChildren: 0.1, delayChildren: 0.5 }}
          className="absolute inset-0 hidden md:flex items-center justify-start text-left p-6 mt-10 z-40"
        >
          <div className="bg-accent/80 px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 rounded-3xl shadow-2xl w-full max-w-sm mx-auto md:ml-12 hero-content-box-desktop border border-accent relative">
            <motion.p variants={fadeInVariants} className="text-sm md:text-md lg:text-sm uppercase text-amber-100 font-semibold mb-2 tracking-wide">
              <span className="bg-black px-2 py-1 rounded-l-lg">{godrejShettigereContent.heroSection.bookingStatus}</span>
              <span className="bg-amber-600 text-black px-2 py-1 rounded-r-lg">{godrejShettigereContent.heroSection.bookingStatusHighlight}</span>
            </motion.p>
            <motion.h1 variants={fadeInVariants} className="text-4xl md:text-5xl lg:text-4xl text-white font-extrabold mb-2 leading-tight tracking-wide">{godrejShettigereContent.heroSection.title}</motion.h1>
            <motion.p variants={fadeInVariants} className="text-md md:text-lg lg:text-base text-white mb-4">{godrejShettigereContent.heroSection.subtitle}</motion.p>
            <motion.hr variants={fadeInVariants} className="border-yellow-300 my-4" />
            <motion.h2 variants={fadeInVariants} className="text-xl md:text-2xl lg:text-xl text-white font-bold mb-3">{godrejShettigereContent.heroSection.apartmentType}</motion.h2>
            <motion.p variants={fadeInVariants} className="text-lg md:text-xl lg:text-lg text-yellow-300 font-bold mb-6">Starts from <span className="text-white">{godrejShettigereContent.heroSection.price}</span> {godrejShettigereContent.heroSection.priceSuffix}</motion.p>
            <motion.ul variants={staggerContainerVariants} className="text-white text-sm md:text-base lg:text-sm space-y-2 mb-6">
              {godrejShettigereContent.heroSection.keyHighlights.map((item, index) => (
                <motion.li key={index} variants={fadeInVariants} className="flex items-center"><span className="mr-2 text-yellow-300">&#9656;</span>{item}</motion.li>
              ))}
            </motion.ul>
            <motion.p variants={fadeInVariants} className="text-white text-xs md:text-sm lg:text-xs mb-6">{godrejShettigereContent.heroSection.rera}</motion.p>
            <motion.div variants={fadeInVariants} className="relative z-50">
              <a href={`tel:${godrejShettigereContent.heroSection.phoneNumber.replace(/\s/g, '')}`} className="bg-yellow-400 text-accent font-bold py-3 px-4 rounded-full flex flex-wrap items-center justify-center text-sm sm:text-base hover:bg-yellow-300 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <Phone className="w-5 h-5 mr-1 mb-1 sm:mb-0" />
                {godrejShettigereContent.heroSection.phoneNumber}
              </a>
            </motion.div>
          </div>
        </motion.div>

        <div className="absolute bottom-4 inset-x-0 z-10">
          <div className="flex justify-center space-x-2">
            {godrejShettigereContent.heroSection.slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 transition-all duration-300 rounded-full ${
                  index === currentSlide
                    ? 'w-8 bg-accent'
                    : 'w-2 bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-8 px-6 md:hidden">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView="visible"
          viewport={{ once: false, amount: 0.1 }}
          variants={fadeInVariants}
          className="max-w-sm mx-auto bg-accent/80 px-4 py-6 rounded-3xl shadow-2xl border border-accent relative"
        >
          <p className="text-sm uppercase text-amber-100 font-semibold mb-2 tracking-wide">
            <span className="bg-black px-2 py-1 rounded-l-lg">{godrejShettigereContent.heroSection.bookingStatus}</span>
            <span className="bg-amber-600 text-black px-2 py-1 rounded-r-lg">{godrejShettigereContent.heroSection.bookingStatusHighlight}</span>
          </p>
          <h1 className="text-3xl text-white font-extrabold mb-2 leading-tight">{godrejShettigereContent.heroSection.title}</h1>
          <p className="text-md text-white mb-4">{godrejShettigereContent.heroSection.subtitle}</p>
          <hr className="border-yellow-300 my-4" />
          <h2 className="text-xl text-white font-bold mb-3">{godrejShettigereContent.heroSection.apartmentType}</h2>
          <p className="text-lg text-yellow-300 font-bold mb-6">Starts from <span className="text-white">{godrejShettigereContent.heroSection.price}</span> {godrejShettigereContent.heroSection.priceSuffix}</p>
          <ul className="text-white text-sm space-y-2 mb-6">
            {godrejShettigereContent.heroSection.keyHighlights.map((item, index) => (
              <li key={index} className="flex items-center"><span className="mr-2 text-yellow-300">&#9656;</span>{item}</li>
            ))}
          </ul>
          <p className="text-white text-xs mb-6">{godrejShettigereContent.heroSection.rera}</p>
          <div className="relative z-50">
            <a href={`tel:${godrejShettigereContent.heroSection.phoneNumber.replace(/\s/g, '')}`} className="bg-yellow-400 text-accent font-bold py-3 px-4 rounded-full flex flex-wrap items-center justify-center text-sm hover:bg-yellow-300 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              <Phone className="w-5 h-5 mr-1 mb-1 sm:mb-0" />
              {godrejShettigereContent.heroSection.phoneNumber}
            </a>
          </div>
        </motion.div>
      </section>

      {/* Project Snapshot Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="py-16 px-6 bg-white" >
        <div className="max-w-7xl mx-auto">
          <motion.p variants={fadeInVariants} className="text-center text-sm uppercase text-gray-500 font-semibold mb-3 tracking-wider">{godrejShettigereContent.projectSnapshot.heading}</motion.p>
          <motion.h2 variants={fadeInVariants} className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 text-center mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">
            {godrejShettigereContent.projectSnapshot.subheading} <span className="text-accent">{godrejShettigereContent.projectSnapshot.altsubheading}</span>
          </motion.h2>

          <motion.div variants={fadeInVariants} className="mt-12 overflow-hidden">
            <div className="bg-accent rounded-xl text-white p-6 md:p-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              {godrejShettigereContent.projectSnapshot.details.map((detail, index) => (
                <div key={index} className={`text-center flex-1 ${index > 0 ? 'md:border-l-2 border-white/30' : ''} px-4`}>
                  <p className="text-xs uppercase mb-1">{detail.type}</p>
                  <p className="text-lg font-bold">{detail.value}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeInVariants} className="mt-6 overflow-hidden">
            <div className="bg-accent rounded-xl text-white p-6 md:p-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">

              {godrejShettigereContent.projectSnapshot.summary.map((item, index) => (
                <div key={index} className={`text-center flex-1 ${index > 0 ? 'md:border-l-2 border-white/30' : ''} px-4`}>
                  <p className="text-4xl sm:text-5xl font-extrabold">{item.value}</p>
                  <p className="text-sm sm:text-base uppercase">{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* About Project Section */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-7xl mx-auto flex flex-col-reverse lg:flex-row items-center gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="lg:w-1/2">
            <motion.h3 variants={fadeInVariants} className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 mb-4 tracking-tight relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">{godrejShettigereContent.aboutProject.heading}</motion.h3>
            {godrejShettigereContent.aboutProject.text.map((paragraph, index) => (
              <motion.p variants={fadeInVariants} key={index} className="text-gray-600 mb-4 leading-relaxed">{paragraph}</motion.p>
            ))}
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.8 }} className="lg:w-1/2 relative overflow-hidden">
            <img src={godrejShettigereContent.aboutProject.image} alt="Godrej Shettigere Project View" className="w-full h-full object-cover rounded-xl" />
          </motion.div>
        </div>
      </section>

      {/* Floor Plans Section */}
      <section className="bg-white py-16 px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-7xl mx-auto text-center">
          <motion.h2 variants={fadeInVariants} className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">
            {godrejShettigereContent.floorPlans.heading}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {godrejShettigereContent.floorPlans.plans.map((plan, index) => (
              <motion.div variants={fadeInVariants} key={index} className="overflow-hidden transition-all duration-300 hover:scale-105">
                <img src={plan.image} alt={plan.bhk} className="w-full h-auto object-cover" />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.bhk}</h3>
                  <p className="text-accent font-semibold">{plan.area}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Gallery Section */}
      <Gallery
        godrejShettigereContent={godrejShettigereContent}
        fadeInVariants={fadeInVariants}
        staggerContainerVariants={staggerContainerVariants}
      />

      {/* Key Highlights Section */}
      <section className="bg-white py-16 px-6 shadow-inner">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-7xl mx-auto">
          <motion.h2 variants={fadeInVariants} className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 text-center mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">
            {godrejShettigereContent.keyHighlights.heading}
          </motion.h2>
          {/* Modified grid layout for larger screens */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-8">
            {godrejShettigereContent.keyHighlights.points.map((point, index) => {
              const IconComponent = keyHighlightIcons[point.title];
              return (
                <motion.div
                  variants={fadeInVariants}
                  key={index}
                  className="flex flex-col items-center text-center"
                >
                  {IconComponent && (
                    <div className="text-accent p-3 bg-accent/10 rounded-full mb-4">
                      <IconComponent className="w-8 h-8" />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{point.title}</h3>
                  <p className="text-sm text-gray-600">{point.subtitle}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Location Map Section */}
      <section className="bg-white py-16 px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-7xl mx-auto">
          <motion.h2 variants={fadeInVariants} className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 text-center mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">{godrejShettigereContent.locationMap.heading}</motion.h2>
          <motion.div variants={fadeInVariants} className="overflow-hidden mb-8 rounded-lg shadow-lg">
            <img src={godrejShettigereContent.locationMap.image} alt="Location Map" className="w-full h-auto object-cover" />
          </motion.div>
          <motion.div variants={staggerContainerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {godrejShettigereContent.locationMap.connectivity.map((item, index) => {
              const iconKey = item.icon === 'Briefcase' ? 'Car' : item.icon;
              const IconComponent = iconMap[iconKey];
              return (
                <motion.div variants={fadeInVariants} key={index} className="flex items-center space-x-4">
                  <div className="text-accent p-3 bg-accent/10 rounded-full">
                    {IconComponent && <IconComponent className="w-6 h-6" />}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{item.label}</h4>
                    <p className="text-sm text-gray-600">{item.distance}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </section>

      {/* Site Plan Section */}
      <section className="bg-gray-50 py-16 px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-7xl mx-auto text-center">
          <motion.h2 variants={fadeInVariants} className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 mb-6 relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">{godrejShettigereContent.sitePlan.heading}</motion.h2>
          <motion.div variants={fadeInVariants} className="overflow-hidden rounded-lg shadow-lg">
            <img src={godrejShettigereContent.sitePlan.image} alt="Master Site Plan" className="w-full h-auto object-cover" />
          </motion.div>
        </motion.div>
      </section>

      {/* About Developer Section */}
      <section className="bg-white py-16 px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <motion.div variants={fadeInVariants} className="lg:w-1/3 flex justify-center">
            <div className="p-6">
              <img src={godrejShettigereContent.aboutDeveloper.logo} alt={godrejShettigereContent.aboutDeveloper.name} className="max-h-24 w-auto" />
            </div>
          </motion.div>
          <motion.div variants={staggerContainerVariants} className="lg:w-2/3">
            <motion.h2 variants={fadeInVariants} className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-500 mb-4 tracking-tight relative pb-4 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-24 after:h-1 after:bg-gradient-to-r after:from-accent after:to-orange-500 after:rounded-full">{godrejShettigereContent.aboutDeveloper.heading}</motion.h2>
            <motion.h3 variants={fadeInVariants} className="text-xl font-semibold text-accent mb-4">{godrejShettigereContent.aboutDeveloper.name}</motion.h3>
            <motion.p variants={fadeInVariants} className="text-gray-600 leading-relaxed">{godrejShettigereContent.aboutDeveloper.text}</motion.p>
          </motion.div>
        </motion.div>
      </section>


      {/* Pre-Book Now Section */}
      <section className="bg-gray-50 py-16 px-6  text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.1 }} variants={staggerContainerVariants} className="max-w-3xl mx-auto">
          <motion.h2 variants={fadeInVariants} className="text-4xl font-extrabold text-accent mb-4">{godrejShettigereContent.prebookSection.heading}</motion.h2>
          <motion.p variants={fadeInVariants} className="text-gray-600 mb-8 leading-relaxed">{godrejShettigereContent.prebookSection.text}</motion.p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <motion.button
            variants={fadeInVariants}
            whileHover={{ scale: 1.05 }}
            onClick={() => setIsScheduleVisitDialogOpen(true)}
            className="bg-accent text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg hover:bg-primary-700 transition-all duration-300 w-full sm:w-auto"
            >
             Schedule a Site Visit
           </motion.button>
           <motion.button
           variants={fadeInVariants}
           whileHover={{ scale: 1.05 }}
           onClick={() => setIsBrochureDialogOpen(true)}
           className="bg-gray-800 text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg  transition-all duration-300 w-full sm:w-auto"
           >
           Download Brochure
          </motion.button>
          </div>
         </motion.div>
         </section>
         </div>
  );
}